import './ActiveAlarmScreen.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefetchIcon from '@mui/icons-material/ReplaySharp';
import AlarmsList from 'src/component/AlarmsList';
import CustomStatistics from '../../../component/UI/Statistics/CustomStatistics';
import FilterBar from 'src/component/UI/FilterBar';
import { fetchDevicesInAlarmWithoutNotification, fetchAlarmStats, fetchAlarmHistoricalSummary, fetchActiveAlarms, dismissAlarms, acknowledgeAlarms } from 'src/module/alarm/action';
import { getAlarms, getAlarmStats, getAlarmStatsLoading, getCriticalAlarmsWithoutNotification, getLoadingAlarms, getNumAlarmPages, getTotalAlarms } from 'src/module/alarm/selector';
import { celsiusToFahrenheitConvertor, isWhitelabel } from 'src/utils/utils';
import { getDevicesSelectedFilters } from 'src/module/filter/selector';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { getClientId, getSingularWorkTicketLabel, getTemperatureUnit } from 'src/module/authentication/selector';
import useMediaQuery from '@mui/material/useMediaQuery';
import DeviceRotate from 'src/component/UI/DeviceRotate';
import variables from 'src/style/variable/variables.module.scss';
import Button from 'src/component/UI/Button';
import LivePill from 'src/component/UI/LivePill';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'src/i18n';

export default function ActiveAlarmScreen () {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const REFRESH_INTERVAL_MS = 60000;
  const [alarmRefreshTick, setAlarmRefreshTick] = useState(0);

  const alarmStats = useSelector(getAlarmStats);
  const currentTemp = alarmStats.getIn(['currentSiteTemperature', 'current'], null);
  const criticalAlarmsWithoutNotification = useSelector(getCriticalAlarmsWithoutNotification);

  const clientId = useSelector(getClientId);
  const selectedFilters = useSelector(getDevicesSelectedFilters);
  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);
  const temperatureUnit = useSelector(getTemperatureUnit);

  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [showRotateMessage, setShowRotateMessage] = useState(isMobile && isPortrait);

  useEffect(() => {
    setShowRotateMessage(isMobile && isPortrait);
  }, [isMobile, isPortrait]);

  const refreshStats = useCallback(() => {
    dispatch(fetchAlarmStats(selectedFilters));
    dispatch(fetchDevicesInAlarmWithoutNotification(selectedFilters));
    dispatch(fetchAlarmHistoricalSummary(selectedFilters, moment().subtract(12, 'weeks').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')));
  }, [dispatch, selectedFilters]);

  const isLoadingStats = useSelector(getAlarmStatsLoading);
  const isLoadingAlarms = useSelector(getLoadingAlarms);
  const manualRefreshPendingRef = useRef(false);
  const wasLoadingRef = useRef(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState<number | null>(null);

  const handleTooltipOpen = useCallback(() => {
    setSecondsAgo(lastFetchedAt ? Math.floor((Date.now() - lastFetchedAt.getTime()) / 1000) : null);
  }, [lastFetchedAt]);
  const refreshing = manualRefreshPendingRef.current && (isLoadingStats || isLoadingAlarms);
  if (manualRefreshPendingRef.current && !isLoadingStats && !isLoadingAlarms) {
    manualRefreshPendingRef.current = false;
  }
  const isLoading = isLoadingStats || isLoadingAlarms;
  if (wasLoadingRef.current && !isLoading) {
    setLastFetchedAt(new Date());
  }
  wasLoadingRef.current = isLoading;

  const handleManualRefresh = useCallback(() => {
    manualRefreshPendingRef.current = true;
    setAlarmRefreshTick((tick: number) => tick + 1);
    refreshStats();
  }, [refreshStats]);

  useEffect(() => {
    refreshStats();
  }, [refreshStats, clientId]);

  useEffect(() => {
    const id = setInterval(refreshStats, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshStats]);

  const variable = {
    alarmStats,
    criticalAlarmsWithoutNotification,
    currentSiteTemperature: currentTemp ? temperatureUnit === 'C' ? `${currentTemp}°C` : celsiusToFahrenheitConvertor(currentTemp) + '°F' : 'N/A',
  };

  const defaultLayout = [
    {
      type: 'grid', container: true, spacing: 3, children: [
        { "type": "grid", "feature": "alarm-historian", "container": false, "md": 4, "xs": 12, "children": [
          { "type": "block", "title": t('dashboard.alarmOverview'), "children": [
            { "type": "minor", "title": t('dashboard.totalAlarms'), "value": "alarmStats.totalAlarms", "helpText": t('dashboard.totalAlarmsHelp'), "defaultValue": "N/A" },
            { "type": "minor", "title": t('dashboard.htcsInAlarm'), "value": "alarmStats.htcsInAlarm", "helpText": t('dashboard.htcsInAlarmHelp'), "defaultValue": "N/A" },
            { "type": "minor", "title": t('dashboard.htcsInCritAlarm'), "value": "alarmStats.criticalAlarms", "helpText": t('dashboard.htcsInCritAlarmHelp'), "defaultValue": "N/A" }
          ] }
        ], fallback: [
          { "type": "block", className: 'no-permission-block', "title": t('dashboard.alarmOverview'), "children": [
            { "type": "minor", "title": t('dashboard.totalAlarms'), "value": "N/A", "helpText": t('dashboard.totalAlarmsHelp'), "defaultValue": "N/A" },
            { "type": "minor", "title": t('dashboard.htcsInAlarm'), "value": "N/A", "helpText": t('dashboard.htcsInAlarmHelp'), "defaultValue": "N/A" },
            { "type": "minor", "title": t('dashboard.htcsInCritAlarm'), "value": "N/A", "helpText": t('dashboard.htcsInCritAlarmHelp'), "defaultValue": "N/A" }
          ] }
        ] },
        { "type": "grid", "feature": "criticality-ruleset", "container": false, "md": 4, "xs": 12, "children": [
          { "type": "block", "title": t('dashboard.criticalAlarms'), "children": [
            { "type": "component", "componentName": "CriticalAlarms" }
          ] }
        ], fallback: [
          { "type": "block", "className": "no-permission-block", "title": t('dashboard.criticalAlarms'), "children": [
            { "type": "major", "title": t('dashboard.newCriticalAlarmsToday'), "value": "N/A", "defaultValue": "N/A" },
            { "type": "major", "title": t('dashboard.alarmsComparedToYesterday'), "value": "N/A", "defaultValue": "N/A" },
          ] }
        ] },
        showRotateMessage ?
          {
            type: 'grid', container: false, xs: 12, md: 4, children: [
              {
                type: 'block', title: t('dashboard.alarmsByStatus'), children: [
                  { type: 'component', componentName: 'DeviceRotate' },
                ]
              }
            ]
          }
          : {
            type: 'grid', container: false, xs: 12, md: 4, children: [
              {
                type: 'block', title: t('dashboard.alarmsByStatus'), children: [
                  { type: 'component', componentName: 'AlarmsByStatusGraph' },
                ]
              }
            ]
          },
      ]
    }

  ];
  const [deviceFilters, setDeviceFilters] = React.useState(() => []);
  const [showFilters, setShowFilters] = React.useState(() => false);

  const handleFormat = (
    event: React.MouseEvent<HTMLElement>,
    newDeviceFilters: string[],
  ) => {
    setDeviceFilters(newDeviceFilters);
  };
  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';



  return (
    <div id='alarms-active' className='page'>
      <Helmet>
        <title>{title} - {t('alarms.activeAlarms')}</title>
      </Helmet>

      <Grid container spacing={3}>

        <FilterBar includeGrid />

        <Grid item xs={12} md={12}>
          <div className='alarms-title-row'>
            <h2 className='alarms-title-row__title'>{t('alarms.activeAlarms')}</h2>
            <div className='alarms-title-row__actions'>
              <Tooltip title={t('ui.refreshNow')}>
                <div className='alarms-title-row__refresh-btn' onClick={refreshing ? undefined : handleManualRefresh}>
                  <RefetchIcon style={{ fontSize: 16, color: 'white' }} />
                </div>
              </Tooltip>
              <Tooltip title={secondsAgo !== null ? t('alarms.lastFetched', { time: secondsAgo }) : ''} onOpen={handleTooltipOpen}>
                <div style={{ display: 'inline-flex' }}>
                  <LivePill label={t('common.live')} />
                </div>
              </Tooltip>

            </div>
          </div>
        </Grid>

        <Grid item xs={12} md={12}>
          <CustomStatistics
            variables={variable}
            defaultLayout={defaultLayout}
          />
        </Grid>


        <Grid item xs={12} md={12}>
          <Paper>
            <div style={{ display: 'flex', height: '45px' }}>
              <Tooltip title={deviceFilters.length !== 0 ? t('alarms.activeAlarmsWhereDeviceDoesNotHave', { types: deviceFilters.join(' or ') }) : t('alarms.allActiveAlarms')} >

                <div>
                  <Button onClick={() => setShowFilters(!showFilters)} className='filterButton'>
                    <FilterListIcon style={{ fontSize: 30, }} />
                  </Button>
                </div>
              </Tooltip>
              {showFilters ?<div>
                <h5>{t('alarms.devicesThatDontHave')}</h5>
                <ToggleButtonGroup
                  value={deviceFilters}
                  onChange={handleFormat}
                  aria-label="alarm filters"
                  className='alarmFilterButtons'
                  color="primary"
                >
                  <ToggleButton className='ToggleButton-text' value="loto" aria-label="loto">
                    {t('alarms.loto')}
                  </ToggleButton>
                  <ToggleButton className='ToggleButton-text' value="nuisance" aria-label="nuisance">
                    {t('alarms.nuisance')}
                  </ToggleButton>
                  <ToggleButton className='ToggleButton-text' value="note" aria-label="note">
                    {t('alarms.note')}
                  </ToggleButton>
                  <ToggleButton className='ToggleButton-text' value="workTickets" aria-label="workTickets">
                    {singularWorkTicketLabel || t('workTickets.singular')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>: null }

            </div>
            {showRotateMessage ? <><h3>{t('alarms.activeAlarms')}</h3><DeviceRotate /></> :
              <AlarmsList
                fetchAlarms={fetchActiveAlarms}
                alarmSelector={getAlarms}
                alarmLoadingSelector={getLoadingAlarms}
                numOfAlarmPagesSelector={getNumAlarmPages}
                numOfAlarmsSelector={getTotalAlarms}
                dismissAlarm={dismissAlarms}
                acknowledgeAlarm={acknowledgeAlarms}
                pagination
                showAlarmRaisedAt
                showSetting
                showValue
                showAlarm
                showActualTemp
                showMaintainTemp
                showActualCurrent
                showActualGFI
                showNote={!isMobile}
                showNotification={!isMobile}
                showDownloadStatsOption
                showClearAlarmOption
                showAcknowledgeAlarmOption
                header={t('alarms.activeAlarms')}
                tableId='activeAlarmsTable'
                deviceFilters={deviceFilters}
                refetchInterval={60000}
                defaultSort='-alarm_raised_at'
                refreshTrigger={alarmRefreshTick}
              />
            }
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

